import React, { useCallback } from 'react';
import Lozenge from '@atlaskit/lozenge';
import Spinner from '@atlaskit/spinner';
import Link from '@atlaskit/link';
import { useIntl } from '@atlassian/jira-intl';
import messages from '../messages.tsx';
import { ERRORS, type ApplinksError } from '../../../../common/status/errors.tsx';
import type { ApplicationLinkV3Status } from '../../../../common/entities.tsx';

export type ApplinkStatusCategory =
	| 'incompatible'
	| 'non-atlassian'
	| 'system'
	| 'network-error'
	| 'recreation-needed'
	| 'partially-configured'
	| 'setting-up'
	| 'partially-connected'
	| 'resubmission-required'
	| 'access-denied'
	| 'config-error'
	| 'deprecated-status'
	| 'disabled-status'
	| 'connected-status';

type StatusLozengeLoadingProps = {
	kind: 'loading';
};

type StatusLozengeErrorProps = {
	kind: 'error';
	err: ApplinksError;
	authorisationUri?: string;
};

type StatusLozengeWorkingProps = {
	kind: 'working';
};

type StatusLozengeNotConnectedProps = {
	kind: 'not-connected';
};

type StatusLozengeProps =
	| StatusLozengeLoadingProps
	| StatusLozengeErrorProps
	| StatusLozengeWorkingProps
	| StatusLozengeNotConnectedProps;

// Use StatusLozengeV3 if you have an ApplicationLinkV3Status, otherwise
// use this one to fake it (e.g. for the creation flow when we don't have
// an applink yet)
export const StatusLozenge = (props: StatusLozengeProps) => {
	const { formatMessage } = useIntl();

	if (props.kind === 'loading') {
		return <Spinner size="small" />;
	}

	if (props.kind === 'working') {
		return (
			<Lozenge appearance="success">{formatMessage(messages.connectedStatusNonfinal)}</Lozenge>
		);
	}

	if (props.kind === 'not-connected') {
		return <Lozenge>{formatMessage(messages.notConnectedStatusNonfinal)}</Lozenge>;
	}

	if (props.kind === 'error') {
		const { err } = props;

		if (err.category === 'ACCESS_ERROR') {
			if (err.type === 'INSUFFICIENT_REMOTE_PERMISSION') {
				return (
					<Lozenge appearance="moved">{formatMessage(messages.accessDeniedStatusNonfinal)}</Lozenge>
				);
			}
			// Authenticated status API -> prompt user to do OAuth dance
			if (props.authorisationUri) {
				return (
					<Link href={props.authorisationUri}>
						{formatMessage(messages.authTokenRequiredNonfinal)}
					</Link>
				);
			}
			// Fallback for ACCESS_ERROR cases that don't match above conditions
			return (
				<Lozenge appearance="removed">{formatMessage(messages.accessDeniedStatusNonfinal)}</Lozenge>
			);
		}

		switch (err.type) {
			case 'INCOMPATIBLE':
				return (
					<Lozenge appearance="default">
						{formatMessage(messages.incompatibleStatusNonfinal)}
					</Lozenge>
				);
			case 'NON_ATLASSIAN':
				return (
					<Lozenge appearance="default">
						{formatMessage(messages.nonAtlassianStatusNonfinal)}
					</Lozenge>
				);
			case 'SYSTEM':
				return (
					<Lozenge appearance="default">{formatMessage(messages.systemStatusNonfinal)}</Lozenge>
				);
			case 'NETWORK_ERROR':
			case 'UNKNOWN':
				return (
					<Lozenge appearance="removed">
						{formatMessage(messages.networkErrorStatusNonfinal)}
					</Lozenge>
				);
			case 'RECREATION_NEEDED':
				return (
					<Lozenge appearance="removed">
						{formatMessage(messages.recreationNeededStatusNonfinal)}
					</Lozenge>
				);
			case 'PARTIALLY_CONFIGURED':
				return (
					<Lozenge appearance="removed">
						{formatMessage(messages.partiallyConfiguredStatusNonfinal)}
					</Lozenge>
				);
			case 'SETTING_UP':
				return (
					<Lozenge appearance="moved">{formatMessage(messages.settingUpStatusNonfinal)}</Lozenge>
				);
			case 'PARTIALLY_CONNECTED':
				return (
					<Lozenge appearance="inprogress">
						{formatMessage(messages.partiallyConnectedStatusNonfinal)}
					</Lozenge>
				);
			case 'RESUBMISSION_REQUIRED':
				return (
					<Lozenge appearance="removed">
						{formatMessage(messages.resubmissionRequiredStatusNonfinal)}
					</Lozenge>
				);
			case 'DEPRECATED':
				return (
					<Lozenge appearance="inprogress">
						{formatMessage(messages.deprecatedStatusNonfinal)}
					</Lozenge>
				);
			case 'DISABLED':
				return (
					<Lozenge appearance="default">{formatMessage(messages.disabledStatusNonfinal)}</Lozenge>
				);
			case 'CONFIG_ERROR':
			default:
				return (
					<Lozenge appearance="removed">
						{formatMessage(messages.configErrorStatusNonfinal)}
					</Lozenge>
				);
		}
	}
};

type StatusLozengeV3Props = {
	status?: ApplicationLinkV3Status | null;
};

export const StatusLozengeV3 = (props: StatusLozengeV3Props) => {
	const wrapWithV3Status = useCallback(() => {
		if (!props.status) {
			return <StatusLozenge kind="loading" />;
		}

		if (props.status.working) {
			return <StatusLozenge kind="working" />;
		}

		const err = props.status.error ?? ERRORS.UNKNOWN;
		return <StatusLozenge kind="error" err={err} />;
	}, [props.status]);

	return wrapWithV3Status();
};
